<script setup>
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'
import { useDashboardStore } from '@/stores/dashboard'

const props = defineProps({
  dashboard: {
    type: Object,
    required: true
  },
  isSubscribed: {
    type: Boolean,
    default: false
  },
  onFork: {
    type: Function,
    required: true
  }
})

const emit = defineEmits(['modal-close'])
const toast = useToast()
const dashboardStore = useDashboardStore()
const forking = ref(false)
const keepSubscription = ref(props.isSubscribed)

const widgets = computed(() => props.dashboard.widgets || [])

async function doFork() {
  if (forking.value) return

  forking.value = true
  try {
    const forked = await dashboardStore.forkDashboard(props.dashboard.id, {
      keep_subscription: keepSubscription.value
    })
    props.onFork(forked)
    emit('modal-close')
  } catch (e) {
    toast.error(e.message || 'Failed to fork dashboard')
  } finally {
    forking.value = false
  }
}
</script>

<template>
  <div class="fork-modal">
    <h3 class="modal-title">Fork Dashboard</h3>
    <p class="modal-description">This will create an editable copy of the dashboard in your account.</p>

    <div class="fork-summary">
      <div class="summary-row">
        <span class="summary-label">Name</span>
        <span class="summary-value">{{ dashboard.name }}</span>
      </div>
      <div v-if="dashboard.description" class="summary-row">
        <span class="summary-label">Description</span>
        <span class="summary-value">{{ dashboard.description }}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">Widgets</span>
        <span class="summary-value">{{ widgets.length }}</span>
      </div>
    </div>

    <div v-if="isSubscribed" class="subscription-toggle">
      <label class="toggle-row" @click="keepSubscription = !keepSubscription">
        <span class="toggle-label">
          <font-awesome-icon :icon="['fas', 'link']" class="toggle-icon" />
          Stay subscribed to original
        </span>
        <span class="toggle-switch" :class="{ 'is-active': keepSubscription }">
          <span class="toggle-knob"></span>
        </span>
      </label>
      <p class="toggle-hint">{{ keepSubscription ? 'You will receive updates from the original dashboard' : 'You will only have your forked copy' }}</p>
    </div>

    <div class="modal-actions">
      <button
        class="btn-fork"
        :disabled="forking"
        @click="doFork"
      >
        <font-awesome-icon v-if="forking" :icon="['fas', 'spinner']" spin />
        <font-awesome-icon v-else :icon="['fas', 'code-fork']" />
        {{ forking ? 'Forking...' : 'Fork' }}
      </button>
      <button class="btn-close-modal" @click="emit('modal-close')">
        Cancel
      </button>
    </div>
  </div>
</template>

<style scoped>
.fork-modal {
  width: 500px;
  max-width: 90vw;
}

.modal-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
}

.modal-description {
  font-size: 0.8125rem;
  color: var(--text-muted);
  margin: 0 0 1rem 0;
}

.fork-summary {
  padding: 0.75rem;
  background: rgba(115, 87, 210, 0.06);
  border: 1px solid rgba(115, 87, 210, 0.15);
  border-radius: var(--radius-md);
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0;
}

.summary-row + .summary-row {
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.summary-label {
  font-size: 0.8125rem;
  color: var(--text-muted);
}

.summary-value {
  font-size: 0.8125rem;
  color: var(--text-primary);
  font-weight: 500;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1rem;
}

.btn-fork {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--accent-soft-3);
  border: 1px solid rgba(115, 87, 210, 0.3);
  border-radius: var(--radius-sm);
  color: var(--accent);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
}

.btn-fork:hover:not(:disabled) {
  background: var(--accent-soft-4);
  border-color: rgba(115, 87, 210, 0.5);
}

.btn-fork:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-close-modal {
  padding: 0.5rem 1rem;
  background: var(--surface-overlay-2);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  color: var(--text-muted);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
}

.btn-close-modal:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-primary);
}

.subscription-toggle {
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: var(--radius-md);
}

.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: var(--text-primary);
  font-weight: 500;
}

.toggle-icon {
  font-size: 0.75rem;
  color: var(--accent);
  opacity: 0.7;
}

.toggle-switch {
  position: relative;
  width: 2.25rem;
  height: 1.25rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md);
  transition: background 0.2s ease;
  flex-shrink: 0;
}

.toggle-switch.is-active {
  background: rgba(115, 87, 210, 0.5);
}

.toggle-knob {
  position: absolute;
  top: 0.125rem;
  left: 0.125rem;
  width: 1rem;
  height: 1rem;
  background: var(--text-muted);
  border-radius: var(--radius-pill);
  transition: var(--transition);
}

.toggle-switch.is-active .toggle-knob {
  left: calc(100% - 1.125rem);
  background: var(--accent);
}

.toggle-hint {
  font-size: 0.6875rem;
  color: var(--text-faint);
  margin: 0.375rem 0 0 0;
}
</style>
